import type { AppThemeConfig } from "@/types/Event";

/**
 * Derived in-phone palette — the SINGLE source of truth for every color the
 * attendee-app preview paints inside the phone bezel. Pure module, no React.
 *
 * PARITY CONTRACT: the derivation rules mirror the device verbatim —
 * attendee-app-mobile/lib/theme/event_theme_builder.dart (EventThemeBuilder
 * .build) and Flutter's ThemeData.estimateBrightnessForColor. A literal copy
 * ships in attendee-app-backend/src/lib/theme-palette.ts; all three repos pin
 * the same fixture (appThemePalette.fixture.json). Change a rule here ⇒
 * regenerate BOTH fixtures + the Dart port in the same change set.
 *
 * Cockpit tokens (surface-*, font-*, outline-*) must NEVER appear inside the
 * phone — they track the ORGANIZER's light/dark mode, not the attendee theme.
 */

/** Device defaults — mirror theme-dto.ts DEFAULT_THEME / EventThemeBuilder. */
export const DEVICE_DEFAULT_PRIMARY = "#2563EB";
export const DEVICE_DEFAULT_BACKGROUND = "#FFFFFF";
export const DEVICE_INK_LIGHT = "#111827"; // Flutter defaultHeading
export const DEVICE_INK_DARK = "#FFFFFF";

export type AppPalette = {
  /** WCAG polarity of surface0 (Flutter estimateBrightnessForColor rule). */
  isDark: boolean;
  /** Page background — theme background or device default white. */
  surface0: string;
  /** Card surface — blend(ink@4%, surface0); device cardTheme color. */
  surface1: string;
  /** Raised surface (placeholders, discs) — blend(ink@8%, surface0). */
  surface2: string;
  /** Hairline border — rgba(ink, 0.08); device card side. */
  outline: string;
  /** Full-strength ink — theme text or polarity default. Hex. */
  ink: string;
  /** Body copy — rgba(ink, 0.72); device bodyMedium. */
  inkSecondary: string;
  /** Labels / inactive nav — rgba(ink, 0.64); device labelSmall. */
  inkMuted: string;
  primary: string;
  /** Ink over solid primary fills — polarity of primary (Flutter onPrimary). */
  onPrimary: string;
  /** Tonal primary wash — countdown cells, icon discs. Pre-blended solid.
   *  POLARITY-AWARE: light surfaces get blend(primary@10%, surface0); dark
   *  surfaces get a white 7% lift first, then primary 6% — a bare 10% primary
   *  over a near-black page is invisible (A2 dark-tint boost; identical math
   *  in the worker copy + the Dart primaryTintOver). */
  primaryTint: string;
  /** 12% primary wash — tab-bar active pill; device indicatorColor. */
  primaryContainer: string;
  /** Legible ink over primaryContainer (primary if it passes, else flipped). */
  onPrimaryContainer: string;
  accent: string;
  onAccent: string;
  /** Tab-bar surface — blend(ink@4%, surface0); device navigationBarTheme. */
  navBar: string;
};

type Rgb = { r: number; g: number; b: number };

/** Parse #RGB / #RRGGBB / #RRGGBBAA (leading # required on the wire; be
 *  tolerant of its absence like the device parser). Garbage → null. */
export function parseHex(input: string | undefined | null): Rgb | null {
  if (!input) return null;
  let hex = input.trim();
  if (hex.startsWith("#")) hex = hex.slice(1);
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  if (hex.length !== 6 && hex.length !== 8) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

const toHex2 = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");
const rgbToHex = ({ r, g, b }: Rgb) => `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toUpperCase();

/** WCAG relative luminance (sRGB-linearized) — Flutter Color.computeLuminance. */
export function relLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 1; // garbage reads as light — matches undefined-theme default
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/** WCAG contrast ratio, order-independent. */
export function contrastRatio(a: string, b: string): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Perceived-dark — Flutter's estimateBrightnessForColor threshold: LIGHT iff
 *  (L + 0.05)² > 0.15. NOT the Y′ 0.5 rule; they disagree on mid greys/reds,
 *  and the device is the ground truth. */
export function isDarkColor(hex: string): boolean {
  const l = relLuminance(hex);
  return (l + 0.05) * (l + 0.05) <= 0.15;
}

/** Composite `fg` at `alpha` over opaque `bg` → opaque hex6 (Color.alphaBlend). */
export function blend(fg: string, alpha: number, bg: string): string {
  const f = parseHex(fg) ?? { r: 0, g: 0, b: 0 };
  const g = parseHex(bg) ?? { r: 255, g: 255, b: 255 };
  return rgbToHex({
    r: f.r * alpha + g.r * (1 - alpha),
    g: f.g * alpha + g.g * (1 - alpha),
    b: f.b * alpha + g.b * (1 - alpha),
  });
}

const rgba = (hex: string, alpha: number): string => {
  const c = parseHex(hex) ?? { r: 0, g: 0, b: 0 };
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
};

/** `fg` if it clears `min` contrast against `bg`; else the polarity ink
 *  (#FFFFFF / #111827) that passes; if neither passes, whichever of the two
 *  inks contrasts harder. Single legibility gate for every tinted ink. */
export function ensureContrast(fg: string, bg: string, min = 4.5): string {
  if (contrastRatio(fg, bg) >= min) return fg;
  const dark = contrastRatio(DEVICE_INK_LIGHT, bg);
  const light = contrastRatio(DEVICE_INK_DARK, bg);
  if (light >= min && light >= dark) return DEVICE_INK_DARK;
  if (dark >= min) return DEVICE_INK_LIGHT;
  return light >= dark ? DEVICE_INK_DARK : DEVICE_INK_LIGHT;
}

/** Parse-gate AND NORMALIZE to opaque #RRGGBB. The save gate allows 8-digit
 *  theme colors (#RRGGBBAA) and legacy data may carry 3-digit — every palette
 *  consumer assumes exactly 7 chars (tint tokens concatenate "1A", polarity
 *  math parses channels), so an unnormalized value would emit 11-char garbage
 *  CSS and run contrast against the wrong surface. Alpha is deliberately
 *  dropped: a translucent THEME color has no meaning on the phone root. */
export const normalizeHex6 = (h: string | undefined): string | undefined => {
  const rgb = parseHex(h);
  return rgb ? rgbToHex(rgb) : undefined;
};
const valid = normalizeHex6;

// Themes are tiny and few per session; a keyed cache keeps deriveAppPalette
// referentially stable across renders without useMemo plumbing at call sites.
const cache = new Map<string, AppPalette>();
const CACHE_CAP = 64;

/** Theme → full in-phone palette. Garbage-safe, never throws; undefined theme
 *  yields the device-default light palette. */
export function deriveAppPalette(theme: AppThemeConfig | undefined): AppPalette {
  const c = theme?.colors ?? {};
  const key = `${c.primary ?? ""}|${c.background ?? ""}|${c.text ?? ""}|${c.accent ?? ""}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const surface0 = valid(c.background) ?? DEVICE_DEFAULT_BACKGROUND;
  const isDark = isDarkColor(surface0);
  const ink = valid(c.text) ?? (isDark ? DEVICE_INK_DARK : DEVICE_INK_LIGHT);
  const primary = valid(c.primary) ?? DEVICE_DEFAULT_PRIMARY;
  const accent = valid(c.accent) ?? primary;
  const primaryContainer = blend(primary, 0.12, surface0);

  const pal: AppPalette = {
    isDark,
    surface0,
    surface1: blend(ink, 0.04, surface0),
    surface2: blend(ink, 0.08, surface0),
    outline: rgba(ink, 0.08),
    ink,
    inkSecondary: rgba(ink, 0.72),
    inkMuted: rgba(ink, 0.64),
    primary,
    onPrimary: isDarkColor(primary) ? DEVICE_INK_DARK : DEVICE_INK_LIGHT,
    primaryTint: isDark
      ? blend(primary, 0.06, blend("#FFFFFF", 0.07, surface0))
      : blend(primary, 0.1, surface0),
    primaryContainer,
    onPrimaryContainer: ensureContrast(primary, primaryContainer),
    accent,
    onAccent: isDarkColor(accent) ? DEVICE_INK_DARK : DEVICE_INK_LIGHT,
    navBar: blend(ink, 0.04, surface0),
  };
  if (cache.size >= CACHE_CAP) cache.clear();
  cache.set(key, pal);
  return pal;
}
